import {
  trackAiVoiceCustomPhraseAuditionCompleted,
  trackAiVoiceCustomPhraseAuditionFailed,
  trackAiVoiceCustomPhraseAuditionStarted,
} from '@src/analytics/EventTracker';
import {
  PrimaryButton,
  SecondaryButton,
} from '@src/components/common/dialogStyles';
import ItemDropdownSelect from '@src/components/common/ItemDropdownSelect';
import Textarea from '@src/components/common/Textarea';
import { DubbingApiError } from '@src/repositories/DubbingRepository';
import { useStore } from '@src/stores-v2/StoreContext';
import { useVideoCreatorStore } from '@src/stores-v2/VideoCreatorStoreContext';
import DownloadIcon from '@src/svgs/DownloadIcon';
import { extractErrorInfo, isAbortError } from '@src/utility/error';
import { LogManager } from '@src/utility/Logger';
import { observer } from 'mobx-react-lite';
import { useEffect, useMemo, useRef, useState } from 'react';
import styled, { css } from 'styled-components';

import AudioSamplePlayer from './AudioSamplePlayer';
import { languageGroups } from './languageGroups';
import MultiSelectFilter from './MultiSelectFilter';
import { providerLabel } from './voiceLabels';
import VoiceSelect from './VoiceSelect';

import type { MultiSelectOption } from './MultiSelectFilter';
import type {
  AuditionHistoryEntry,
  VoiceTestResult,
} from '@src/repositories/DubbingRepository';
import type { AiVoiceRecord } from '@src/types.ts/aiVoiceRecord';

const logger = LogManager.getLogger('aiVoices:VoiceAuditionPanel');

const MAX_TEXT_LENGTH = 500;

// Default language selection: let the provider auto-detect the language from the
// typed text (which is what the pinned models do regardless). Sent as the
// `language` value; the provider ignores it, and it only tags the S3 temp path.
const AUTO_LANGUAGE = 'auto';

// Workspace facet: filter the picker to voices already activated in the showcase
// vs. those not yet added.
const WORKSPACE_OPTIONS: MultiSelectOption[] = [
  { value: 'included', label: 'Included' },
  { value: 'excluded', label: 'Not included' },
];

// Shared "Audition a voice" surface (DE9 / PLAT-10504): select a voice → type a
// phrase → pick a language → Audition → hear the temporary utterance via the
// ad-hoc word-generation API (PLAT-10496). Built in Settings; reused unchanged
// by the speaker pop-over "AI Voices" tab (PLAT-10503). Owns its own transient
// state and calls DubbingRepository directly — DubbingStore stays a pure catalog.
interface VoiceAuditionPanelProps {
  /** The selectable voice pool (Settings passes the full active catalog). */
  voices: AiVoiceRecord[];
  /** Preselect a voice (used by the speaker tab); defaults to the first voice. */
  initialVoiceId?: string;
  /**
   * Controlled selected voice (S1): when provided, the parent owns selection so
   * the audition panel and the voice card stay in sync. Omit for standalone,
   * self-managed use (the speaker-tab reuse, T14).
   */
  voiceId?: string;
  onVoiceChange?: (voiceId: string) => void;
  /**
   * Optional Add/Remove control shown beside the Voice picker — toggles the
   * selected voice in the showcase's activated pool. Omitted where activation
   * isn't relevant (e.g. the speaker-tab reuse).
   */
  activation?: {
    isActivated: boolean;
    onToggle: () => void;
    busy?: boolean;
    disabled?: boolean;
  };
  /**
   * Overrides the "Workspace" membership facet. When provided, the picker check
   * and the Included / Not-included filter reflect THIS set (e.g. the voices on
   * a speaker) under `label`, instead of the workspace activation pool. Omit for
   * the default workspace behavior (Settings).
   */
  membership?: {
    label: string;
    ids: Set<string>;
    /** Filter option labels for in / not in the set (default Included / Not included). */
    options?: { included: string; excluded: string };
    /** Hover label on the picker check for voices in the set (default "In workspace"). */
    checkLabel?: string;
    /** Add / Remove button labels (default "Add" / "Remove"). */
    addLabel?: string;
    removeLabel?: string;
  };
  /**
   * Fired when an audition is persisted (PLAT-10580) — i.e. the backend
   * returned an `auditionId`. Lets the parent prepend the just-completed
   * audition to the history list optimistically. Omitted where history isn't
   * shown (e.g. the speaker-tab reuse).
   */
  onAuditionComplete?: (entry: AuditionHistoryEntry) => void;
  /**
   * Grow the phrase textarea to fill the panel's height (up to the Voice row
   * above and the Language/Audition row below). Used where the panel is given a
   * full-height column (the speaker Manage modal); omit in Settings, where the
   * panel sizes to its content.
   */
  fillHeight?: boolean;
}

function friendlyError(error: unknown): string {
  if (error instanceof DubbingApiError) {
    if (error.code === 'TEXT_TOO_LONG') {
      return `Phrase is too long (max ${MAX_TEXT_LENGTH} characters).`;
    }
    if (error.code === 'MISSING_CREDENTIALS') {
      return 'Couldn’t reach the voice provider. Please try again later.';
    }
    if (error.status === 404) return 'That voice is no longer available.';
    // Other API errors carry a server message worth surfacing.
    const info = extractErrorInfo(error);
    if (info?.errorMessage) return info.errorMessage;
  }
  // Non-API errors (network, parse, …) shouldn't leak a raw `Error: …` string
  // to the user; show a generic message instead.
  return 'Audition failed. Please try again.';
}

const VoiceAuditionPanel = observer(
  ({
    voices,
    initialVoiceId,
    voiceId: controlledVoiceId,
    onVoiceChange,
    activation,
    membership,
    onAuditionComplete,
    fillHeight = false,
  }: VoiceAuditionPanelProps) => {
    const { datoClientStore } = useStore();
    const videoCreator = useVideoCreatorStore();
    const workspaceLanguages = useMemo(
      () => videoCreator.organization?.supportedLanguages ?? [],
      [videoCreator.organization?.supportedLanguages],
    );
    // Voices activated in the showcase — surfaced as a check in the picker and a
    // "Workspace: Included / Not included" filter.
    const activatedIds = useMemo(
      () =>
        new Set(
          (videoCreator.organization?.activatedAiVoices ?? []).map(v => v.id),
        ),
      [videoCreator.organization?.activatedAiVoices],
    );
    // The membership facet reflects the speaker's voices when `membership` is
    // given, else the workspace activation pool.
    const membershipIds = membership?.ids ?? activatedIds;
    const membershipLabel = membership?.label ?? 'Workspace';
    const membershipOptions: MultiSelectOption[] = membership?.options
      ? [
          { value: 'included', label: membership.options.included },
          { value: 'excluded', label: membership.options.excluded },
        ]
      : WORKSPACE_OPTIONS;

    const isControlled = controlledVoiceId !== undefined;
    const [internalVoiceId, setInternalVoiceId] = useState<string>(
      initialVoiceId ?? voices[0]?.id ?? '',
    );
    const voiceId = isControlled ? controlledVoiceId : internalVoiceId;
    const setVoiceId = (id: string) => {
      if (!isControlled) setInternalVoiceId(id);
      onVoiceChange?.(id);
    };
    const [text, setText] = useState('');
    const [language, setLanguage] = useState(AUTO_LANGUAGE);
    const [isAuditioning, setIsAuditioning] = useState(false);
    const [result, setResult] = useState<VoiceTestResult | null>(null);
    const [error, setError] = useState<string | null>(null);

    // Voice filters (multi-select): narrow the picker by gender, provider, and
    // supported language. Empty = no filter for that facet.
    const [genderFilter, setGenderFilter] = useState<string[]>([]);
    const [languageFilter, setLanguageFilter] = useState<string[]>([]);
    const [providerFilter, setProviderFilter] = useState<string[]>([]);
    const [workspaceFilter, setWorkspaceFilter] = useState<string[]>([]);

    const abortRef = useRef<AbortController | null>(null);

    const selectedVoice = useMemo(
      () => voices.find(v => v.id === voiceId) ?? null,
      [voices, voiceId],
    );

    // Filter facet options, derived from the pool (only values actually present).
    const genderOptions = useMemo<MultiSelectOption[]>(() => {
      const present = new Set(voices.map(v => v.gender).filter(Boolean));
      return (['male', 'female', 'neutral'] as const)
        .filter(g => present.has(g))
        .map(g => ({
          value: g,
          label: g.charAt(0).toUpperCase() + g.slice(1),
        }));
    }, [voices]);

    const providerOptions = useMemo<MultiSelectOption[]>(
      () =>
        [...new Set(voices.map(v => v.provider))].map(p => ({
          value: p,
          label: providerLabel(p),
        })),
      [voices],
    );

    const languageOptions = useMemo<MultiSelectOption[]>(() => {
      const byValue = new Map<string, string>();
      voices.forEach(v =>
        v.supportedLanguages.forEach(l => byValue.set(l.value, l.caption)),
      );
      return [...byValue.entries()]
        .map(([value, label]) => ({ value, label }))
        .sort((a, b) => a.label.localeCompare(b.label));
    }, [voices]);

    // A voice passes a facet when nothing is selected there, or it matches one
    // of the selected values (language = supports ANY selected language).
    const filteredVoices = useMemo(() => {
      const g = new Set(genderFilter);
      const p = new Set(providerFilter);
      const l = new Set(languageFilter);
      const w = new Set(workspaceFilter);
      return voices.filter(v => {
        if (g.size && !(v.gender && g.has(v.gender))) return false;
        if (p.size && !p.has(v.provider)) return false;
        if (l.size && !v.supportedLanguages.some(lang => l.has(lang.value)))
          return false;
        if (w.size) {
          const included = membershipIds.has(v.id);
          if (
            !(
              (included && w.has('included')) ||
              (!included && w.has('excluded'))
            )
          )
            return false;
        }
        return true;
      });
    }, [
      voices,
      genderFilter,
      providerFilter,
      languageFilter,
      workspaceFilter,
      membershipIds,
    ]);

    // When the filters exclude the current voice, jump to the first match so the
    // picker + card stay on a voice that satisfies the filters.
    useEffect(() => {
      if (
        filteredVoices.length > 0 &&
        !filteredVoices.some(v => v.id === voiceId)
      ) {
        setVoiceId(filteredVoices[0].id);
      }
      // setVoiceId is derived inline; the guard makes re-runs idempotent.
    }, [filteredVoices, voiceId]);

    // Keep the selected voice valid if the pool changes underneath us. Only in
    // self-managed mode — when controlled, the parent owns selection validity.
    useEffect(() => {
      if (isControlled) return;
      if (voices.length > 0 && !voices.some(v => v.id === voiceId)) {
        setInternalVoiceId(initialVoiceId ?? voices[0].id);
      }
    }, [voices, voiceId, initialVoiceId, isControlled]);

    // Language groups (S5): the voice's supported languages, split into
    // workspace-active (highlighted, first) vs the rest (shared with the card).
    const { workspaceGroup, otherGroup } = useMemo(
      () =>
        languageGroups(
          selectedVoice?.supportedLanguages ?? [],
          workspaceLanguages,
        ),
      [selectedVoice, workspaceLanguages],
    );

    const allLanguages = useMemo(
      () => [...workspaceGroup, ...otherGroup],
      [workspaceGroup, otherGroup],
    );

    // "Automatic" (auto-detect) is always valid and the default. If a specific
    // language was picked and then disappears (voice change), fall back to auto.
    useEffect(() => {
      if (language === AUTO_LANGUAGE) return;
      if (!allLanguages.some(l => l.value === language)) {
        setLanguage(AUTO_LANGUAGE);
      }
    }, [allLanguages, language]);

    // Cancel any in-flight audition on unmount.
    useEffect(() => () => abortRef.current?.abort(), []);

    const trimmedText = text.trim();
    const canAudition =
      !isAuditioning && !!selectedVoice && trimmedText.length > 0 && !!language;

    const handleAudition = async () => {
      if (!selectedVoice || !language || trimmedText.length === 0) return;

      // Re-auditioning supersedes any in-flight request.
      abortRef.current?.abort();
      const controller = new AbortController();
      abortRef.current = controller;

      setIsAuditioning(true);
      setError(null);
      setResult(null);

      const analyticsBase = {
        ai_voice_id: selectedVoice.id,
        provider: selectedVoice.provider,
        language,
      };
      trackAiVoiceCustomPhraseAuditionStarted({
        ...analyticsBase,
        text_length: trimmedText.length,
      });

      try {
        const res = await datoClientStore.dubbingRepository.voiceTest({
          aiVoiceId: selectedVoice.id,
          language,
          text: trimmedText,
          // Scope the audition to the workspace so the backend persists a
          // durable, replayable record (PLAT-10579); omitted → throwaway.
          showcaseId: videoCreator.organization?.id,
          signal: controller.signal,
        });
        if (controller.signal.aborted) return;
        setResult(res);
        trackAiVoiceCustomPhraseAuditionCompleted({
          ...analyticsBase,
          text_length: trimmedText.length,
          duration_ms: res.durationMs,
        });
        // Persisted (auditionId returned) → let the parent prepend it to the
        // history list without waiting for a refetch.
        if (res.auditionId) {
          onAuditionComplete?.({
            auditionId: res.auditionId,
            aiVoiceId: selectedVoice.id,
            provider: selectedVoice.provider,
            language,
            text: trimmedText,
            durationMs: res.durationMs,
            createdAt: new Date().toISOString(),
            audioUrl: res.audioUrl,
            // Provenance is server-resolved (never trusted from the client);
            // this optimistic row shows no author until the next history load.
            createdByName: null,
          });
        }
      } catch (err) {
        // Abort is a user action (unmount / re-audition), not a failure.
        if (isAbortError(err)) return;
        const message = friendlyError(err);
        setError(message);
        logger.error('audition failed', extractErrorInfo(err));
        trackAiVoiceCustomPhraseAuditionFailed({
          ...analyticsBase,
          error_message: message,
        });
      } finally {
        if (abortRef.current === controller) {
          setIsAuditioning(false);
          abortRef.current = null;
        }
      }
    };

    // The audio URL is a cross-origin signed URL, so the anchor `download`
    // attribute is ignored — fetch the bytes and download the blob instead.
    const handleDownload = async () => {
      if (!result) return;
      try {
        const response = await fetch(result.audioUrl);
        const blob = await response.blob();
        const objectUrl = URL.createObjectURL(blob);
        const anchor = document.createElement('a');
        anchor.href = objectUrl;
        // Filename-safe timestamp of the moment of download (colons/dots → -).
        const stamp = new Date().toISOString().replace(/[:.]/g, '-');
        anchor.download = `audition-${selectedVoice?.internalName ?? 'voice'}-${language}-${stamp}.${result.format}`;
        document.body.appendChild(anchor);
        anchor.click();
        anchor.remove();
        URL.revokeObjectURL(objectUrl);
      } catch (err) {
        logger.warn('audition download failed', extractErrorInfo(err));
      }
    };

    if (voices.length === 0) {
      return (
        <EmptyState>
          No AI Voices are configured. Add voices to the catalog to audition
          them.
        </EmptyState>
      );
    }

    return (
      <Container $fill={fillHeight}>
        <FiltersRow>
          <MultiSelectFilter
            label="Gender"
            options={genderOptions}
            selected={genderFilter}
            onChange={setGenderFilter}
            disabled={isAuditioning}
          />
          <MultiSelectFilter
            label="Language"
            options={languageOptions}
            selected={languageFilter}
            onChange={setLanguageFilter}
            searchable
            minWidth={300}
            disabled={isAuditioning}
          />
          <MultiSelectFilter
            label="Provider"
            options={providerOptions}
            selected={providerFilter}
            onChange={setProviderFilter}
            disabled={isAuditioning}
          />
          <MultiSelectFilter
            label={membershipLabel}
            options={membershipOptions}
            selected={workspaceFilter}
            onChange={setWorkspaceFilter}
            disabled={isAuditioning}
          />
        </FiltersRow>

        <Field>
          <FieldLabel htmlFor="audition-voice">Voice</FieldLabel>
          <div className="voice-row">
            <VoiceSelect
              id="audition-voice"
              voices={filteredVoices}
              value={voiceId}
              onChange={setVoiceId}
              disabled={isAuditioning}
              activatedIds={membershipIds}
              activatedLabel={membership?.checkLabel}
            />
            {activation &&
              (() => {
                const Button = activation.isActivated
                  ? RemoveMemberButton
                  : AddMemberButton;
                return (
                  <Button
                    type="button"
                    onClick={activation.onToggle}
                    disabled={
                      activation.disabled ||
                      activation.busy ||
                      !selectedVoice ||
                      // Don't act on a voice the current filters have hidden.
                      !filteredVoices.some(v => v.id === voiceId)
                    }
                  >
                    {activation.isActivated
                      ? (membership?.removeLabel ?? 'Remove')
                      : (membership?.addLabel ?? 'Add')}
                  </Button>
                );
              })()}
          </div>
          {filteredVoices.length === 0 && (
            <span className="no-match">No voices match these filters.</span>
          )}
        </Field>

        <Textarea
          label="Phrase to audition"
          aria-label="Phrase to audition"
          fullWidth
          value={text}
          maxLength={MAX_TEXT_LENGTH}
          placeholder="Type a phrase to hear this voice say it…"
          onChange={e => setText(e.target.value)}
          disabled={isAuditioning}
          helperText={`${text.length}/${MAX_TEXT_LENGTH}`}
        />

        <ActionRow>
          <Field className="language-field">
            <FieldLabel id="audition-language-label">Language</FieldLabel>
            <LanguagePicker
              $disabled={isAuditioning}
              aria-labelledby="audition-language-label"
            >
              <ItemDropdownSelect
                propertyName="audition-language"
                defaultValue={language}
                action={(_name, value) => setLanguage(value ?? AUTO_LANGUAGE)}
                values={[
                  {
                    value: AUTO_LANGUAGE,
                    caption: 'Automatic (detect from text)',
                  },
                  ...(workspaceGroup.length > 0
                    ? [
                        {
                          value: 'heading:workspace',
                          caption: 'In this workspace',
                          isHeading: true,
                        },
                        ...workspaceGroup.map(l => ({
                          value: l.value,
                          caption: l.caption,
                        })),
                      ]
                    : []),
                  ...(otherGroup.length > 0
                    ? [
                        {
                          value: 'heading:other',
                          caption: 'Other languages this voice supports',
                          isHeading: true,
                        },
                        ...otherGroup.map(l => ({
                          value: l.value,
                          caption: l.caption,
                        })),
                      ]
                    : []),
                ]}
              />
            </LanguagePicker>
          </Field>
          {(() => {
            // One green button per panel: with an Add/Remove button beside the
            // picker, adding the voice is the goal and Audition steps down.
            const Button = activation ? AuditionSecondary : AuditionPrimary;
            return (
              <Button
                type="button"
                onClick={handleAudition}
                disabled={!canAudition}
              >
                {isAuditioning ? 'Auditioning…' : 'Audition'}
              </Button>
            );
          })()}
        </ActionRow>

        {error && <ErrorText role="alert">{error}</ErrorText>}
        {result && (
          <ResultRow>
            <AudioSamplePlayer url={result.audioUrl} autoPlay />
            <button
              type="button"
              className="download-btn"
              onClick={handleDownload}
              aria-label="Download audition"
              title="Download audition"
            >
              <DownloadIcon
                width="18"
                height="18"
                strokeColor="var(--foreground)"
              />
            </button>
          </ResultRow>
        )}
      </Container>
    );
  },
);

export default VoiceAuditionPanel;

const Container = styled.div<{ $fill: boolean }>`
  display: flex;
  flex-direction: column;
  gap: var(--spacing-md);

  /* When given a full-height column, the phrase field grows to fill the slack
     between the Voice row above and the Language/Audition row below; its
     textarea fills the field. A no-op where the panel sizes to content. */
  ${p =>
    p.$fill
      ? `
    height: 100%;
  `
      : ''}
`;

const FiltersRow = styled.div`
  display: flex;
  flex-wrap: wrap;
  gap: 8px;
`;

const ActionRow = styled.div`
  display: flex;
  align-items: flex-end;
  gap: var(--spacing-sm);

  .language-field {
    flex: 1;
  }
`;

const ResultRow = styled.div`
  display: flex;
  align-items: center;
  gap: 12px;

  > div {
    flex: 1;
    min-width: 0;
  }

  .download-btn {
    display: inline-flex;
    align-items: center;
    justify-content: center;
    width: 44px;
    height: 44px;
    flex-shrink: 0;
    border: 1px solid var(--border);
    border-radius: 8px;
    background: transparent;
    cursor: pointer;
    transition: var(--transition-normal);

    &:hover {
      border-color: var(--text-muted);
      background: var(--surface-elevated);
    }
  }
`;

const Field = styled.div`
  display: flex;
  flex-direction: column;
  gap: var(--spacing-xs);

  .no-match {
    color: var(--text-muted);
    font-size: var(--text-xs);
  }

  .voice-row {
    display: flex;
    align-items: center;
    gap: var(--spacing-sm);
  }
  .voice-row > *:first-child {
    flex: 1;
    min-width: 0;
  }
`;

// The design system's buttons beside the voice picker. Fixed width so Add and
// Remove render identically (no layout shift); Remove is the neutral Secondary
// that only turns destructive on hover.
// Design-system sizes below lg: the panel is a form, not a dialog footer. The
// button beside the voice picker is an inline control (sm); Audition, the
// panel's own action, is md.
const smButton = css`
  height: var(--btn-h-sm);
  padding: 0 var(--btn-px-sm);
  font-size: var(--text-xs);
`;
const mdButton = css`
  height: var(--btn-h-md);
  padding: 0 var(--btn-px-md);
`;

const AddMemberButton = styled(PrimaryButton)`
  ${smButton}
  flex-shrink: 0;
`;

// Neutral Secondary (design system: secondary is never coloured); removing a
// voice from a speaker is reversible, so it is not a destructive confirm.
const RemoveMemberButton = styled(SecondaryButton)`
  ${smButton}
  flex-shrink: 0;
`;

// The same label the shared Input/Textarea primitives draw, for the two fields
// whose control is a dropdown (which brings no label of its own).
const FieldLabel = styled.label`
  font-family: var(--font-primary);
  font-size: var(--text-xs);
  font-weight: var(--font-weight-medium);
  line-height: var(--leading-text-xs);
  color: var(--text-secondary);
`;

const AuditionPrimary = styled(PrimaryButton)`
  ${mdButton}
`;
const AuditionSecondary = styled(SecondaryButton)`
  ${mdButton}
`;

// The DS dropdown has no disabled state of its own; the panel freezes it while
// a sample is being synthesised.
const LanguagePicker = styled.div<{ $disabled: boolean }>`
  ${p =>
    p.$disabled &&
    css`
      pointer-events: none;
      opacity: 0.6;
    `}
  .Selected {
    box-sizing: border-box;
    height: var(--btn-h-md);
    padding: 0 12px;
    border-radius: var(--radius-input);
  }
`;

const ErrorText = styled.div`
  color: var(--destructive);
  font-size: var(--text-sm);
  line-height: 1.5;
`;

const EmptyState = styled.div`
  padding: 16px;
  border: 1px dashed var(--border);
  border-radius: 8px;
  color: var(--text-muted);
  font-size: var(--text-sm);
`;
